<template>
  <FormCard
    v-for="fieldGroup in props.fieldGroups"
    :key="fieldGroup.key"
    :data-testid="`self-service-form-googleads-search-group-${fieldGroup.key}`"
    :data-group-key="fieldGroup.key"
    :title="fieldGroup.name"
    :class="{ 'selected-card': openCard === fieldGroup.key }"
    :is-open="openCard === fieldGroup.key"
    @toggle="handleFormCardToggle(fieldGroup.key)"
    :completed="completedCards.has(fieldGroup.key)"
    @enter-pressed="handleSaveAndContinue(fieldGroup.key)">
    <template #form-content>
      <div
        class="form-section"
        v-for="field in fieldGroup.properties"
        :key="field.key"
        :data-testid="`self-service-field-${field.key}`"
        :data-field-key="field.key">
        <GoogleAdsTargeting
          v-if="field.type === 'locations' && field.key === 'target_locations' && providerId"
          v-model="targetingValue"
          :provider-id="providerId"
          :errors="errors[field.key] || null" />

        <GoogleMultipleHeadlines
          v-else-if="field.type === 'headlines'"
          v-model="obj[field.key]"
          :options="field.options"
          :errors="errors[field.key] || null" />

        <GoogleMultipleDescriptions
          v-else-if="field.type === 'descriptions'"
          v-model="obj[field.key]"
          :options="field.options"
          :errors="errors[field.key] || null" />

        <LeadSelect
          v-else-if="field.type === 'select'"
          v-model="obj[field.key]"
          :options="field.options || []"
          :label="field.label"
          :errors="errors[field.key] || null" />

        <LeadInput
          v-else-if="field.type === 'text'"
          v-model="obj[field.key]"
          :label="field.label"
          :errors="errors[field.key] || null" />
      </div>
      <div
        class="form-actions float-right"
        :data-testid="`self-service-save-action-${fieldGroup.key}`"
        :data-i18n-key="isLast(fieldGroup.key) ? 'btn.save' : 'btn.save_and_continue'">
        <LeadButton variant="secondary" :loading="loadingSave" @click="handleSaveAndContinue(fieldGroup.key)">
          {{ isLast(fieldGroup.key) ? $t('btn.save') : $t('btn.save_and_continue') }}
          <template v-if="!isLast(fieldGroup.key)" #icon>
            <IconArrowBigRightFilled size="16" stroke-width="1.5" />
          </template>
        </LeadButton>
      </div>
    </template>
  </FormCard>
</template>

<script setup lang="ts">
import { watch, ref, onMounted, computed, nextTick, type Ref } from 'vue';
import FormCard from '@/components/orders/form_cards/FormCard.vue';
import LeadButton from '@/components/generic/LeadButton.vue';
import LeadSelect from '@/components/form/LeadSelect.vue';
import LeadInput from '@/components/form/LeadInput.vue';
import { IconArrowBigRightFilled } from '@tabler/icons-vue';
import { updateCampaignObject } from '@/api/campaign';
import type { FieldGroup } from '@/types/types';
import GoogleAdsTargeting from '@/components/form/GoogleAdsTargeting.vue';
import GoogleMultipleHeadlines from '@/components/form/GoogleMultipleHeadlines.vue';
import GoogleMultipleDescriptions from '@/components/form/GoogleMultipleDescriptions.vue';
import { useProviderStore } from '@/stores/providers';
import { storeToRefs } from 'pinia';

interface GoogleAdsForm {
  target_locations: Array<{
    target_location_label: string;
    target_location_id: number;
    target_country_label: string;
    target_country_code: string;
    bid_modifier: number;
  }>;
  target_proximity_locations: Array<any>;
  location_targeting_type: string;
  language: string;
  headlines: string;
  descriptions: string;
  [key: string]: any;
}

const props = defineProps<{
  providerId: string | null | undefined;
  fieldGroups: FieldGroup[];
  object: any;
}>();

const emit = defineEmits<{
  (e: 'update:object', obj: any): void;
}>();

const openCard = ref<string | null>(null);
const providerStore = useProviderStore();
const { completedDecks } = storeToRefs(providerStore);
const { obj } = storeToRefs(providerStore) as unknown as { obj: Ref<GoogleAdsForm> };
const loadingSave = ref(false);

const completedCards = computed(() => {
  if (!props.providerId) return new Set<string>();
  return completedDecks.value[props.providerId] || new Set<string>();
});
const errors = ref({});

const targetingValue = computed({
  get() {
    return {
      target_locations: obj.value.target_locations || [],
      target_proximity_locations: obj.value.target_proximity_locations || [],
    };
  },
  set(newValue) {
    obj.value.target_locations = newValue.target_locations;
    obj.value.target_proximity_locations = newValue.target_proximity_locations;
  },
});

onMounted(async () => {
  // Open the first card by default if field groups are available
  if (props.fieldGroups.length > 0 && !openCard.value) {
    openCard.value = props.fieldGroups[0].key;
  }
});

const handleFormCardToggle = (key: string) => {
  // If the clicked card is already open, close it
  if (openCard.value === key) {
    openCard.value = null;
  } else {
    // Otherwise, open the clicked card (and close any other open card)
    openCard.value = key;
  }
};

const handleSaveAndContinue = async (currentCard: string) => {
  loadingSave.value = true;
  // Update the object with the values of the current card
  const updateData = {};
  props.object.groups.forEach((group) => {
    if (group.key === currentCard) {
      Object.values(group.properties).map((property: any) => {
        if (property.key === 'target_locations') {
          // For location targeting, save both target_locations and target_proximity_locations
          if (obj.value.target_locations !== undefined) {
            updateData['target_locations'] = obj.value.target_locations;
          }
          if (obj.value.target_proximity_locations !== undefined) {
            updateData['target_proximity_locations'] = obj.value.target_proximity_locations;
          }
        } else if (obj.value[property.key] !== undefined) {
          updateData[property.key] = obj.value[property.key];
        }
      });
    }
  });

  try {
    const res = await updateCampaignObject(props.providerId ?? '', updateData);
    emit('update:object', res.data);

    // Clear any previous errors on success
    errors.value = {};
    setTimeout(() => {
      loadingSave.value = false;
      providerStore.addCompletedCard(currentCard, props.providerId ?? '');

      // If there's a next card to open, set it as the open card
      const currentIndex = props.fieldGroups.findIndex((group) => group.key === currentCard);
      if (currentIndex >= 0 && currentIndex < props.fieldGroups.length - 1) {
        openCard.value = props.fieldGroups[currentIndex + 1].key;
      } else {
        // If no next card, close the current card
        openCard.value = null;
      }
    }, 500);
  } catch (error: any) {
    if (error.responseJson) {
      errors.value = error.responseJson.errors?.provider_values || {};
    } else {
      console.error('Unexpected error:', error);
    }
    loadingSave.value = false;
  }
};

const isLast = (key: string) => {
  const lastIndex = props.fieldGroups.length - 1;
  return props.fieldGroups[lastIndex].key === key;
};

watch(
  () => props.fieldGroups,
  (newValue) => {
    if (newValue) {
      openCard.value = null;
      if (props.fieldGroups.length > 0 && !openCard.value) {
        nextTick(() => {
          openCard.value = props.fieldGroups[0].key;
        });

        props.object.groups.map((group) => {
          if (group.properties) {
            if (props.object?.confirmed || props.object?.values_valid) {
              providerStore.addCompletedCard(group.key, props.providerId ?? '');
            }

            Object.values(group.properties).map((property: any) => {
              obj.value[property.key] = property.value || '';

              nextTick(() => {
                // Ensure at least one default location if both arrays are empty
                if (obj.value?.target_locations.length === 0 && obj.value?.target_proximity_locations.length === 0) {
                  obj.value.target_locations = [
                    {
                      target_location_label: 'Please select a location...',
                      target_location_id: 0,
                      target_country_label: '',
                      target_country_code: '',
                      bid_modifier: 1.0,
                    },
                  ];
                }
              });
            });
          }
        });
      }
    }
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.float-right {
  float: right;
}

.form-section {
  margin-bottom: 30px;
}
</style>
